/**
 * Alquiler de Coches (Car Rentals) page
 * Rental vehicle catalog management with full CRUD
 */

import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { EmptyState } from '../components/ui/empty-state';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  rentalsApi,
  type RentalVehicle,
  type CreateRentalVehicleRequest,
  type FuelType,
  type TransmissionType,
  RENTAL_FEATURES,
  FEATURE_LABELS,
  FUEL_TYPE_LABELS,
  TRANSMISSION_LABELS,
} from '../services/rentalsApi';
import { useToast } from '../contexts/ToastContext';
import { RefreshCw, Car, Plus, ExternalLink, Pencil, Trash2, Image, Link, Fuel, Users, Settings2, MapPin, List, LayoutGrid } from 'lucide-react';
import { cn } from '../lib/utils';

const STATUS_LABELS: Record<string, string> = {
  available: 'Disponible',
  rented: 'Alquilado',
  maintenance: 'Mantenimiento',
};

const STATUS_COLORS: Record<string, string> = {
  available: 'bg-green-100 text-green-800',
  rented: 'bg-blue-100 text-blue-800',
  maintenance: 'bg-yellow-100 text-yellow-800',
};

interface RentalFormData {
  registration: string;
  make: string;
  model: string;
  year: string;
  dailyRate: string;
  status: 'available' | 'rented' | 'maintenance';
  description: string;
  fuelType: FuelType | '';
  transmission: TransmissionType | '';
  seats: string;
  features: string[];
  imageUrls: string[];
  externalLink: string;
  pickupLocation: string;
}

const emptyFormData: RentalFormData = {
  registration: '',
  make: '',
  model: '',
  year: new Date().getFullYear().toString(),
  dailyRate: '',
  status: 'available',
  description: '',
  fuelType: '',
  transmission: '',
  seats: '5',
  features: [],
  imageUrls: [],
  externalLink: '',
  pickupLocation: '',
};

import { pageStyles, tableStyles } from '../lib/styles';

export function RentalsPage() {
  const toast = useToast();

  const [vehicles, setVehicles] = useState<RentalVehicle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<RentalVehicle | null>(null);
  const [formData, setFormData] = useState<RentalFormData>(emptyFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');

  const [deleteVehicle, setDeleteVehicle] = useState<RentalVehicle | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [displayMode, setDisplayMode] = useState<'table' | 'cards'>('table');

  // Load vehicles
  const loadVehicles = useCallback(async () => {
    try {
      // API returns array directly (api.get extracts data property)
      const vehiclesList = await rentalsApi.list();
      setVehicles(vehiclesList || []);
    } catch {
      // Fallback to public endpoint if admin fails
      try {
        const vehiclesList = await rentalsApi.getPublicCatalog();
        setVehicles(vehiclesList || []);
      } catch {
        toast.error('Error al cargar vehiculos de alquiler');
      }
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadVehicles();
  }, [loadVehicles]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadVehicles();
    setIsRefreshing(false);
  };

  const openCreateDialog = () => {
    setEditingVehicle(null);
    setFormData(emptyFormData);
    setNewImageUrl('');
    setIsDialogOpen(true);
  };

  const openEditDialog = (vehicle: RentalVehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      registration: vehicle.registration || '',
      make: vehicle.make || '',
      model: vehicle.model || '',
      year: vehicle.year?.toString() || new Date().getFullYear().toString(),
      dailyRate: vehicle.dailyRate?.toString() || '',
      status: vehicle.status || 'available',
      description: vehicle.description || '',
      fuelType: vehicle.fuelType || '',
      transmission: vehicle.transmission || '',
      seats: vehicle.seats?.toString() || '5',
      features: vehicle.features || [],
      imageUrls: vehicle.imageUrls || [],
      externalLink: vehicle.externalLink || '',
      pickupLocation: vehicle.pickupLocation || '',
    });
    setNewImageUrl('');
    setIsDialogOpen(true);
  };

  const addImageUrl = () => {
    if (newImageUrl.trim() && !formData.imageUrls.includes(newImageUrl.trim())) {
      setFormData({ ...formData, imageUrls: [...formData.imageUrls, newImageUrl.trim()] });
      setNewImageUrl('');
    }
  };

  const removeImageUrl = (url: string) => {
    setFormData({ ...formData, imageUrls: formData.imageUrls.filter((u) => u !== url) });
  };

  const toggleFeature = (feature: string) => {
    const features = formData.features.includes(feature)
      ? formData.features.filter((f) => f !== feature)
      : [...formData.features, feature];
    setFormData({ ...formData, features });
  };

  const handleSave = async () => {
    // Validation
    if (!formData.registration.trim()) {
      toast.error('La matricula es requerida');
      return;
    }
    if (!formData.make.trim()) {
      toast.error('La marca es requerida');
      return;
    }
    if (!formData.model.trim()) {
      toast.error('El modelo es requerido');
      return;
    }
    if (!formData.dailyRate || parseFloat(formData.dailyRate) <= 0) {
      toast.error('La tarifa diaria debe ser mayor que 0');
      return;
    }

    setIsSaving(true);
    try {
      const data: CreateRentalVehicleRequest = {
        registration: formData.registration.trim(),
        make: formData.make.trim(),
        model: formData.model.trim(),
        year: parseInt(formData.year),
        dailyRate: parseFloat(formData.dailyRate),
        status: formData.status,
        description: formData.description.trim() || undefined,
        fuelType: formData.fuelType || null,
        transmission: formData.transmission || null,
        seats: formData.seats ? parseInt(formData.seats) : null,
        features: formData.features.length > 0 ? formData.features : null,
        imageUrls: formData.imageUrls.length > 0 ? formData.imageUrls : null,
        externalLink: formData.externalLink.trim() || null,
        pickupLocation: formData.pickupLocation.trim() || null,
      };

      if (editingVehicle) {
        await rentalsApi.update(editingVehicle.id, data);
        toast.success('Vehiculo actualizado');
      } else {
        await rentalsApi.create(data);
        toast.success('Vehiculo creado');
      }

      setIsDialogOpen(false);
      await loadVehicles();
    } catch (error) {
      toast.error(editingVehicle ? 'Error al actualizar vehiculo' : 'Error al crear vehiculo');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteVehicle) return;

    setIsDeleting(true);
    try {
      await rentalsApi.delete(deleteVehicle.id);
      toast.success('Vehiculo eliminado');
      setDeleteVehicle(null);
      await loadVehicles();
    } catch {
      toast.error('Error al eliminar vehiculo');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className={pageStyles.header}>
        <div>
          <h1 className={pageStyles.title}>Alquiler de Coches</h1>
          <p className={pageStyles.subtitle}>
            Catalogo de vehiculos para alquiler
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex items-center gap-0.5 bg-gray-100 p-0.5 rounded-md">
            <button
              onClick={() => setDisplayMode('table')}
              className={cn('p-1.5 rounded transition-colors', displayMode === 'table' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700')}
              title="Vista tabla"
            >
              <List className="h-4 w-4" />
            </button>
            <button
              onClick={() => setDisplayMode('cards')}
              className={cn('p-1.5 rounded transition-colors', displayMode === 'cards' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700')}
              title="Vista tarjetas"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Actualizar</span>
          </Button>
          <Button size="sm" onClick={openCreateDialog}>
            <Plus className="h-3 w-3 mr-1" />
            <span className="hidden sm:inline">Añadir</span>
          </Button>
        </div>
      </div>

      {/* Info card about public API */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
        <div className="flex items-center gap-2">
          <ExternalLink className="h-3.5 w-3.5 text-blue-600 shrink-0" />
          <p className="text-xs text-blue-700">
            <span className="font-medium">API:</span>{' '}
            <code className="bg-blue-100/50 px-1 py-0.5 rounded text-[10px]">/api/public/rentals</code>{' '}
            muestra vehiculos disponibles sin matricula
          </p>
        </div>
      </div>

      {/* Vehicle list */}
      {isLoading ? (
        <div className={displayMode === 'cards' ? 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3' : `${tableStyles.container} p-3 space-y-2`}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className={displayMode === 'cards' ? 'h-48 bg-gray-100 animate-pulse rounded-lg' : 'h-12 bg-gray-100 animate-pulse rounded'} />
          ))}
        </div>
      ) : vehicles.length === 0 ? (
        <EmptyState
          icon={Car}
          title="Sin vehiculos"
          description="Añade vehiculos al catalogo"
          actionLabel="Añadir vehiculo"
          onAction={openCreateDialog}
          compact
        />
      ) : displayMode === 'cards' ? (
        /* Cards View */
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {vehicles.map((vehicle) => {
            const imageUrl = vehicle.imageUrls?.[0] || vehicle.photoR2Key;
            return (
              <div key={vehicle.id} className="bg-white rounded-lg border shadow-sm hover:shadow-md hover:border-blue-200 transition-all overflow-hidden">
                {/* Image */}
                <div className="h-32 bg-gray-100 flex items-center justify-center overflow-hidden">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={`${vehicle.make} ${vehicle.model}`}
                      className="w-full h-full object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    <Car className="h-10 w-10 text-gray-300" />
                  )}
                </div>

                {/* Content */}
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{vehicle.make} {vehicle.model}</h3>
                      <p className="text-xs text-gray-500">{vehicle.year} • <span className="font-mono">{vehicle.registration}</span></p>
                    </div>
                    <span className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_COLORS[vehicle.status || 'available']}`}>
                      {STATUS_LABELS[vehicle.status || 'available']}
                    </span>
                  </div>

                  {/* Specs */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {vehicle.fuelType && (
                      <span className="flex items-center gap-0.5 bg-gray-100 px-1.5 py-0.5 rounded text-[10px] text-gray-600">
                        <Fuel className="h-2.5 w-2.5" />
                        {FUEL_TYPE_LABELS[vehicle.fuelType]}
                      </span>
                    )}
                    {vehicle.transmission && (
                      <span className="flex items-center gap-0.5 bg-gray-100 px-1.5 py-0.5 rounded text-[10px] text-gray-600">
                        <Settings2 className="h-2.5 w-2.5" />
                        {TRANSMISSION_LABELS[vehicle.transmission]}
                      </span>
                    )}
                    {vehicle.seats && (
                      <span className="flex items-center gap-0.5 bg-gray-100 px-1.5 py-0.5 rounded text-[10px] text-gray-600">
                        <Users className="h-2.5 w-2.5" />
                        {vehicle.seats} plazas
                      </span>
                    )}
                  </div>

                  {/* Price and Actions */}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t">
                    <span className="font-semibold text-green-700">{vehicle.dailyRate?.toFixed(2)} €/día</span>
                    <div className="flex items-center gap-1">
                      {vehicle.externalLink && (
                        <a
                          href={vehicle.externalLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded hover:bg-gray-100 text-blue-500 hover:text-blue-700"
                          title="Abrir enlace"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                      <button
                        onClick={() => openEditDialog(vehicle)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteVehicle(vehicle)}
                        className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Table View */
        <div className={tableStyles.container}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={tableStyles.headerRow}>
                  <th className={tableStyles.headerCell}>Vehiculo</th>
                  <th className={`${tableStyles.headerCell} hidden sm:table-cell`}>Matricula</th>
                  <th className={`${tableStyles.headerCell} hidden md:table-cell`}>Especificaciones</th>
                  <th className={tableStyles.headerCell}>Tarifa</th>
                  <th className={tableStyles.headerCell}>Estado</th>
                  <th className={`${tableStyles.headerCell} text-right`}>Acciones</th>
                </tr>
              </thead>
              <tbody className={tableStyles.body}>
                {vehicles.map((vehicle) => {
                  const imageUrl = vehicle.imageUrls?.[0] || vehicle.photoR2Key;
                  return (
                    <tr key={vehicle.id} className={tableStyles.row}>
                      <td className={tableStyles.cell}>
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-8 rounded bg-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={`${vehicle.make} ${vehicle.model}`}
                                className="w-full h-full object-cover"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                            ) : (
                              <Car className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900 truncate">{vehicle.make} {vehicle.model}</div>
                            <div className="text-[10px] text-gray-500">{vehicle.year}</div>
                            <div className="text-[10px] text-gray-500 sm:hidden font-mono">{vehicle.registration}</div>
                          </div>
                        </div>
                      </td>
                      <td className={`${tableStyles.cell} hidden sm:table-cell`}>
                        <span className="font-mono text-gray-600">{vehicle.registration}</span>
                      </td>
                      <td className={`${tableStyles.cell} hidden md:table-cell`}>
                        <div className="flex flex-wrap gap-1">
                          {vehicle.fuelType && (
                            <span className="flex items-center gap-0.5 bg-gray-100 px-1 py-0.5 rounded text-[10px] text-gray-600">
                              <Fuel className="h-2.5 w-2.5" />
                              {FUEL_TYPE_LABELS[vehicle.fuelType]}
                            </span>
                          )}
                          {vehicle.transmission && (
                            <span className="flex items-center gap-0.5 bg-gray-100 px-1 py-0.5 rounded text-[10px] text-gray-600">
                              <Settings2 className="h-2.5 w-2.5" />
                              {TRANSMISSION_LABELS[vehicle.transmission]}
                            </span>
                          )}
                          {vehicle.seats && (
                            <span className="flex items-center gap-0.5 bg-gray-100 px-1 py-0.5 rounded text-[10px] text-gray-600">
                              <Users className="h-2.5 w-2.5" />
                              {vehicle.seats}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={tableStyles.cell}>
                        <span className="font-medium text-green-700">{vehicle.dailyRate?.toFixed(2)} €/día</span>
                      </td>
                      <td className={tableStyles.cell}>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_COLORS[vehicle.status || 'available']}`}>
                          {STATUS_LABELS[vehicle.status || 'available']}
                        </span>
                      </td>
                      <td className={`${tableStyles.cell} text-right`}>
                        <div className="flex items-center justify-end gap-1">
                          {vehicle.externalLink && (
                            <a
                              href={vehicle.externalLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 rounded hover:bg-gray-100 text-blue-500 hover:text-blue-700"
                              title="Abrir enlace"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          )}
                          <button
                            onClick={() => openEditDialog(vehicle)}
                            className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => setDeleteVehicle(vehicle)}
                            className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">
              {editingVehicle ? 'Editar Vehiculo' : 'Nuevo Vehiculo'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Basic info section */}
            <div className="space-y-3">
              <h3 className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Información Básica</h3>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="registration" className="text-xs">Matrícula *</Label>
                  <Input
                    id="registration"
                    placeholder="1234 ABC"
                    className="h-8 text-sm"
                    value={formData.registration}
                    onChange={(e) => setFormData({ ...formData, registration: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="year" className="text-xs">Año *</Label>
                  <Input
                    id="year"
                    type="number"
                    min="1990"
                    max="2030"
                    className="h-8 text-sm"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="make" className="text-xs">Marca *</Label>
                  <Input
                    id="make"
                    placeholder="Skoda, Toyota..."
                    className="h-8 text-sm"
                    value={formData.make}
                    onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="model" className="text-xs">Modelo *</Label>
                  <Input
                    id="model"
                    placeholder="Fabia, Corolla..."
                    className="h-8 text-sm"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="dailyRate" className="text-xs">Tarifa (€/día) *</Label>
                  <Input
                    id="dailyRate"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="50.00"
                    className="h-8 text-sm"
                    value={formData.dailyRate}
                    onChange={(e) => setFormData({ ...formData, dailyRate: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="status" className="text-xs">Estado</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value as RentalFormData['status'] })}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Disponible</SelectItem>
                      <SelectItem value="rented">Alquilado</SelectItem>
                      <SelectItem value="maintenance">Mantenimiento</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Vehicle specs section */}
            <div className="space-y-3">
              <h3 className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Especificaciones</h3>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="fuelType" className="text-xs flex items-center gap-1">
                    <Fuel className="h-3 w-3" />
                    Combustible
                  </Label>
                  <Select
                    value={formData.fuelType}
                    onValueChange={(value) => setFormData({ ...formData, fuelType: value as FuelType })}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gasoline">Gasolina</SelectItem>
                      <SelectItem value="diesel">Diesel</SelectItem>
                      <SelectItem value="hybrid">Híbrido</SelectItem>
                      <SelectItem value="electric">Eléctrico</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="transmission" className="text-xs flex items-center gap-1">
                    <Settings2 className="h-3 w-3" />
                    Transmisión
                  </Label>
                  <Select
                    value={formData.transmission}
                    onValueChange={(value) => setFormData({ ...formData, transmission: value as TransmissionType })}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="automatic">Automático</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="seats" className="text-xs flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    Plazas
                  </Label>
                  <Input
                    id="seats"
                    type="number"
                    min="1"
                    max="12"
                    className="h-8 text-sm"
                    value={formData.seats}
                    onChange={(e) => setFormData({ ...formData, seats: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Features section */}
            <div className="space-y-2">
              <h3 className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Características</h3>

              <div className="grid grid-cols-3 gap-x-3 gap-y-1.5">
                {RENTAL_FEATURES.map((feature) => (
                  <div key={feature} className="flex items-center space-x-1.5">
                    <Checkbox
                      id={feature}
                      checked={formData.features.includes(feature)}
                      onCheckedChange={() => toggleFeature(feature)}
                      className="h-3.5 w-3.5"
                    />
                    <Label htmlFor={feature} className="text-xs font-normal cursor-pointer">
                      {FEATURE_LABELS[feature]}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Images section */}
            <div className="space-y-2">
              <h3 className="text-[11px] font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1">
                <Image className="h-3 w-3" />
                Imágenes (URLs)
              </h3>

              <div className="flex gap-2">
                <Input
                  placeholder="https://ejemplo.com/imagen.jpg"
                  className="h-8 text-sm"
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addImageUrl())}
                />
                <Button type="button" variant="outline" size="sm" onClick={addImageUrl}>
                  Añadir
                </Button>
              </div>

              {formData.imageUrls.length > 0 && (
                <div className="grid grid-cols-4 gap-2">
                  {formData.imageUrls.map((url, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={url}
                        alt={`Imagen ${idx + 1}`}
                        className="w-full h-14 object-cover rounded border"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '';
                          (e.target as HTMLImageElement).alt = 'Error';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => removeImageUrl(url)}
                        className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="h-2.5 w-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* External link and location */}
            <div className="space-y-3">
              <h3 className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Enlace y Ubicación</h3>

              <div className="space-y-1">
                <Label htmlFor="externalLink" className="text-xs flex items-center gap-1">
                  <Link className="h-3 w-3" />
                  Enlace de reserva
                </Label>
                <Input
                  id="externalLink"
                  type="url"
                  placeholder="https://socialcar.com/es/coche/..."
                  className="h-8 text-sm"
                  value={formData.externalLink}
                  onChange={(e) => setFormData({ ...formData, externalLink: e.target.value })}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="pickupLocation" className="text-xs flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  Ubicación de recogida
                </Label>
                <Input
                  id="pickupLocation"
                  placeholder="41011 Sevilla - Aparcamiento"
                  className="h-8 text-sm"
                  value={formData.pickupLocation}
                  onChange={(e) => setFormData({ ...formData, pickupLocation: e.target.value })}
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-1">
              <Label htmlFor="description" className="text-xs">Descripción</Label>
              <textarea
                id="description"
                placeholder="Descripción del vehículo..."
                value={formData.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button size="sm" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Guardando...' : editingVehicle ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteVehicle} onOpenChange={() => setDeleteVehicle(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">Eliminar Vehículo</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-700 py-2">
            ¿Eliminar <strong>{deleteVehicle?.make} {deleteVehicle?.model}</strong> ({deleteVehicle?.registration})?
          </p>
          <p className="text-xs text-gray-500">
            Esta acción no se puede deshacer.
          </p>
          <DialogFooter className="gap-2 mt-2">
            <Button variant="outline" size="sm" onClick={() => setDeleteVehicle(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" size="sm" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
